"use client";

import { useEffect, useState } from "react";

export type BillingPlan = "free" | "pro" | "agency";

export type CachedAccount = {
  email: string;
  plan: BillingPlan;
  subscriptionStatus: string;
  credits: number;
  monthlyGrant: number;
};

export type AccountState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; token: string; account: CachedAccount };

export const AUTH_TOKEN_KEY = "snap-site:web-auth-token";
export const ACCOUNT_CACHE_KEY = "snap-site:web-account";
export const CLIENT_ID_KEY = "snap-site:web-client-id";
const AUTH_EVENT = "snap-site:auth-changed";

export function useAccountState(): AccountState {
  const [snap, setSnap] = useState<AccountState>({ status: "loading" });

  useEffect(() => {
    function read() {
      if (typeof window === "undefined") return;
      const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) {
        setSnap({ status: "anonymous" });
        return;
      }
      const cachedRaw = window.localStorage.getItem(ACCOUNT_CACHE_KEY);
      if (!cachedRaw) {
        setSnap({ status: "loading" });
        return;
      }
      try {
        const cached = JSON.parse(cachedRaw) as CachedAccount;
        if (!cached?.email) throw new Error("invalid cache");
        setSnap({ status: "authenticated", token, account: cached });
      } catch {
        window.localStorage.removeItem(ACCOUNT_CACHE_KEY);
        setSnap({ status: "loading" });
      }
    }
    read();
    function onAuthChanged() {
      read();
    }
    window.addEventListener(AUTH_EVENT, onAuthChanged);
    window.addEventListener("storage", onAuthChanged);
    return () => {
      window.removeEventListener(AUTH_EVENT, onAuthChanged);
      window.removeEventListener("storage", onAuthChanged);
    };
  }, []);

  return snap;
}

export function writeAuthSession(token: string, account: CachedAccount): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  window.localStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(account));
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export function clearAuthSession(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(ACCOUNT_CACHE_KEY);
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export function readClientId(): string {
  if (typeof window === "undefined") return "web-ssr";
  const existing = window.localStorage.getItem(CLIENT_ID_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `web-${crypto.randomUUID()}`
      : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(CLIENT_ID_KEY, id);
  return id;
}

export function planLabel(plan: BillingPlan): string {
  if (plan === "agency") return "Agency";
  if (plan === "pro") return "Pro";
  return "Free";
}

export function emailInitial(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return "?";
  return trimmed[0]?.toUpperCase() ?? "?";
}
